import { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { CATEGORIES } from "@/lib/categories";
import ClubMap from "@/components/ClubMap";
import { BTN_SM, CARD, FIELD, CHIP, H1, MUTED } from "@/lib/theme";
import { Pizza, ArrowRight, Search, MapPin, LocateFixed, List, Map as MapIcon, Navigation, Check, Users } from "lucide-react";

const MI = 3958.8;
const haversine = (a, b) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return MI * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};
const fmtDist = (d) => (d == null ? null : d < 10 ? `${d.toFixed(1)} mi` : `${Math.round(d)} mi`);
const hasCoords = (c) => typeof c.lat === "number" && typeof c.lng === "number";

const EmptyState = ({ title, children }) => (
  <div className={`${CARD} p-10 text-center`}>
    <span className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-[#D94A3D]/10 text-[#D94A3D]"><Pizza size={28} /></span>
    <p className="mt-4 font-heading font-bold text-lg text-[#17324D]">{title}</p>
    <p className={`${MUTED} mt-1`}>{children}</p>
  </div>
);

export default function Clubs() {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("All");
  const [city, setCity] = useState("All");
  const [q, setQ] = useState("");
  const [view, setView] = useState("list");
  const [userLoc, setUserLoc] = useState(null);
  const [locating, setLocating] = useState(false);
  const [zip, setZip] = useState("");
  const [zipBusy, setZipBusy] = useState(false);
  const [locMsg, setLocMsg] = useState("");

  useEffect(() => {
    api.get("/restaurants").then((r) => setClubs(r.data)).finally(() => setLoading(false));
  }, []);

  const available = useMemo(() => {
    const present = new Set(clubs.map((c) => c.category || "Other"));
    return ["All", ...CATEGORIES.filter((c) => present.has(c))];
  }, [clubs]);

  const cities = useMemo(() => {
    const present = [...new Set(clubs.map((c) => (c.city || "").trim()).filter(Boolean))].sort();
    return ["All", ...present];
  }, [clubs]);

  const filtered = useMemo(() => {
    let list = clubs.filter((c) => {
      const okCat = cat === "All" || (c.category || "Other") === cat;
      const okCity = city === "All" || (c.city || "").trim() === city;
      const okQ = !q.trim() || (c.name || "").toLowerCase().includes(q.trim().toLowerCase());
      return okCat && okCity && okQ;
    });
    if (userLoc) {
      list = list.map((c) => ({ ...c, _dist: hasCoords(c) ? haversine(userLoc, { lat: c.lat, lng: c.lng }) : null }));
      list.sort((a, b) => {
        if (a._dist == null && b._dist == null) return 0;
        if (a._dist == null) return 1;
        if (b._dist == null) return -1;
        return a._dist - b._dist;
      });
    }
    return list;
  }, [clubs, cat, city, q, userLoc]);

  const mapClubs = useMemo(() => filtered.filter(hasCoords), [filtered]);
  const withLocationCount = useMemo(() => clubs.filter(hasCoords).length, [clubs]);

  const useMyLocation = useCallback(() => {
    if (!navigator.geolocation) { setLocMsg("Location isn't supported on this device."); return; }
    setLocating(true); setLocMsg("");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setUserLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocating(false); setView("map"); },
      () => { setLocating(false); setLocMsg("Couldn't get your location — try searching a city or ZIP below."); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const searchZip = async (e) => {
    e.preventDefault();
    if (!zip.trim()) return;
    setZipBusy(true); setLocMsg("");
    try {
      const { data } = await api.get("/geocode", { params: { q: zip.trim() } });
      setUserLoc({ lat: data.lat, lng: data.lng });
      setView("map");
    } catch {
      setLocMsg("Couldn't find that place — try a city name or ZIP code.");
    } finally {
      setZipBusy(false);
    }
  };

  return (
    <div className="App min-h-screen bg-[#FFF4E6] text-[#17324D]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 py-12 md:py-16">
        <p className="text-xs sm:text-sm font-bold tracking-[0.18em] uppercase text-[#D94A3D]">Find your spot</p>
        <h1 className={`${H1} mt-3 mb-8 max-w-3xl`}>Find Restaurant Membership Clubs Near You</h1>

        {!loading && clubs.length > 0 && (
          <div className="mb-10 space-y-4" data-testid="clubs-filters">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
              <button data-testid="use-my-location" onClick={useMyLocation} disabled={locating} className={`${BTN_SM} py-3`}>
                <LocateFixed size={18} /> {locating ? "Locating…" : "Use my location"}
              </button>
              <form onSubmit={searchZip} className={`${FIELD} flex-1`}>
                <span className={`px-3 ${MUTED}`}><Navigation size={18} /></span>
                <input
                  data-testid="zip-search"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  placeholder="Enter a city or ZIP code…"
                  className="flex-1 min-w-0 py-3 pr-3 bg-transparent focus:outline-none placeholder:text-[#17324D]/40"
                />
                <button type="submit" data-testid="zip-search-btn" disabled={zipBusy}
                  className="px-5 py-3 font-semibold text-sm text-white bg-[#17324D] hover:bg-[#0F2338] transition-colors disabled:opacity-60">
                  {zipBusy ? "…" : "Search"}
                </button>
              </form>
              <div className="flex items-center gap-2 shrink-0" data-testid="view-toggle">
                <button data-testid="view-list" onClick={() => setView("list")} className={`${CHIP(view === "list")} inline-flex items-center gap-1.5`}>
                  <List size={16} /> List
                </button>
                <button data-testid="view-map" onClick={() => setView("map")} className={`${CHIP(view === "map")} inline-flex items-center gap-1.5`}>
                  <MapIcon size={16} /> Map
                </button>
              </div>
            </div>
            {locMsg && <p data-testid="loc-msg" className="text-sm text-[#D94A3D] font-medium">{locMsg}</p>}
            {userLoc && (
              <p className="text-sm font-semibold flex items-center gap-1.5" data-testid="loc-active">
                <MapPin size={15} className="text-[#D94A3D]" /> Showing clubs nearest to you first.
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <div className={`${FIELD} flex-1`}>
                <span className={`px-3 ${MUTED}`}><Search size={18} /></span>
                <input
                  data-testid="club-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by restaurant name…"
                  className="flex-1 min-w-0 py-3 pr-3 bg-transparent focus:outline-none placeholder:text-[#17324D]/40"
                />
              </div>
              {cities.length > 1 && (
                <div className={`${FIELD} sm:w-56`}>
                  <span className={`px-3 ${MUTED}`}><MapPin size={18} /></span>
                  <select
                    data-testid="club-city-filter"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="flex-1 min-w-0 py-3 pr-2 bg-transparent focus:outline-none font-semibold"
                  >
                    {cities.map((ct) => <option key={ct} value={ct}>{ct === "All" ? "All cities" : ct}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2" data-testid="category-filters">
              {available.map((c) => (
                <button key={c} data-testid={`cat-chip-${c}`} onClick={() => setCat(c)} className={CHIP(cat === c)}>
                  {c}
                </button>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <p className={MUTED}>Loading clubs...</p>
        ) : clubs.length === 0 ? (
          <EmptyState title="No clubs yet.">
            Are you a restaurant owner? <Link to="/register" className="text-[#D94A3D] font-semibold underline underline-offset-4">Start your club</Link>.
          </EmptyState>
        ) : view === "map" ? (
          <div data-testid="map-view" className={`${CARD} p-3`}>
            {mapClubs.length === 0 && (
              <p className={`mb-3 text-sm ${MUTED}`}>
                No clubs with a set location match your filters yet. {withLocationCount === 0 ? "Owners can add an address in their dashboard to appear on the map." : "Try clearing filters or switch to the list."}
              </p>
            )}
            <div className="rounded-xl overflow-hidden"><ClubMap clubs={mapClubs} userLoc={userLoc} /></div>
            <p className={`mt-3 text-xs ${MUTED}`}>Only clubs with a saved address appear on the map. Tap a pin to view and join.</p>
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState title="No clubs match your filters.">
            <button onClick={() => { setCat("All"); setCity("All"); setQ(""); }} className="text-[#D94A3D] font-semibold underline underline-offset-4">Clear filters</button>
          </EmptyState>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((c, i) => (
              <Link to={`/clubs/${c.id}`} key={c.id} data-testid={`club-card-${c.id}`}
                className={`${CARD} p-7 flex flex-col fade-up hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(23,50,77,0.12)] transition-[transform,box-shadow] duration-200`}
                style={{ animationDelay: `${i * 0.06}s` }}>
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-heading font-bold text-xl md:text-2xl leading-tight">{c.name}</h3>
                  <span className="bg-[#D94A3D] text-white text-xs font-bold px-2.5 py-1 rounded-lg whitespace-nowrap">
                    ${c.monthly_price}/mo
                  </span>
                </div>
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <span className="inline-block bg-[#17324D]/8 text-[#17324D] text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full">
                    {c.category || "Other"}
                  </span>
                  {c.city ? <span className={`flex items-center gap-1 text-xs ${MUTED}`}><MapPin size={12} /> {c.city}</span> : null}
                  {userLoc && c._dist != null && (
                    <span data-testid={`club-dist-${c.id}`} className="flex items-center gap-1 text-xs font-bold text-[#D94A3D]">
                      <Navigation size={12} /> {fmtDist(c._dist)} away
                    </span>
                  )}
                </div>
                <p className={`${MUTED} mt-3 text-sm line-clamp-2`}>{c.description || "A membership club for regulars."}</p>
                <p data-testid={`club-members-${c.id}`} className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-[#17324D] bg-[#FFF4E6] rounded-full px-2.5 py-1 self-start">
                  <Users size={13} className="text-[#D94A3D]" />
                  {c.member_count > 0 ? `${c.member_count} regular${c.member_count === 1 ? "" : "s"} already joined` : "Be the first regular to join"}
                </p>
                <ul className="mt-4 space-y-1.5 flex-1">
                  {(c.perks || []).slice(0, 3).map((p, j) => (
                    <li key={j} className="text-sm flex gap-2 items-start"><Check size={15} strokeWidth={3} className="text-[#D94A3D] mt-0.5 shrink-0" />{p}</li>
                  ))}
                </ul>
                <span className="mt-5 inline-flex items-center gap-1 text-[#D94A3D] font-bold text-sm">
                  View & Join <ArrowRight size={15} />
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
